import { useEffect, useState } from "react";
import axios from "axios";
import { Logo } from "../components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "salonup_admin_token";

const formatDate = (iso) =>
  new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [leads, setLeads] = useState(null);

  const load = async (t) => {
    try {
      const { data } = await axios.get(`${API}/admin/leads`, { headers: { Authorization: `Bearer ${t}` } });
      setLeads(data);
    } catch {
      localStorage.removeItem(TOKEN_KEY);
      setToken("");
    }
  };

  useEffect(() => {
    if (token) load(token);
  }, [token]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Não foi possível entrar.");
    }
  };

  const exportCsv = async () => {
    const { data } = await axios.get(`${API}/admin/leads/export`, {
      headers: { Authorization: `Bearer ${token}` },
      responseType: "blob",
    });
    const url = URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = url;
    a.download = "cadastros-salonup.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-neutral-light px-6">
        <form onSubmit={login} data-testid="admin-login-form" className="w-full max-w-sm rounded-2xl border border-charcoal/10 bg-white p-8">
          <Logo />
          <h1 className="mt-6 font-display text-xl font-medium tracking-tight text-charcoal">Área do administrador</h1>
          <label htmlFor="admin-email" className="mb-1.5 mt-6 block text-sm font-medium text-charcoal">E-mail</label>
          <input id="admin-email" data-testid="admin-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="w-full rounded-xl border border-charcoal/15 px-4 py-3 text-base focus:border-salon focus:outline-none" />
          <label htmlFor="admin-password" className="mb-1.5 mt-4 block text-sm font-medium text-charcoal">Senha</label>
          <input id="admin-password" data-testid="admin-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="w-full rounded-xl border border-charcoal/15 px-4 py-3 text-base focus:border-salon focus:outline-none" />
          {error && <p data-testid="admin-login-error" className="mt-3 text-sm text-red-700">{error}</p>}
          <button type="submit" data-testid="admin-login-submit" className="mt-6 w-full rounded-full bg-salon py-3 text-sm font-medium text-white transition-colors hover:bg-salon-deep">
            Entrar
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-light">
      <div className="mx-auto max-w-5xl px-6 py-12">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <Logo />
          <div className="flex gap-3">
            <button onClick={exportCsv} data-testid="admin-export-button" className="rounded-full bg-salon px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-salon-deep">
              Exportar planilha (CSV)
            </button>
            <button
              onClick={() => { localStorage.removeItem(TOKEN_KEY); setToken(""); }}
              data-testid="admin-logout-button"
              className="rounded-full border border-charcoal/15 px-5 py-2.5 text-sm font-medium text-charcoal/70 transition-colors hover:text-charcoal"
            >
              Sair
            </button>
          </div>
        </div>

        <h1 className="mt-10 font-display text-2xl font-medium tracking-tight text-charcoal">Cadastros da lista de lançamento</h1>
        <p className="mt-1 text-sm text-charcoal/55" data-testid="admin-leads-count">
          {leads ? `${leads.length} cadastro${leads.length === 1 ? "" : "s"}` : "Carregando..."}
        </p>

        <div className="mt-6 overflow-x-auto rounded-2xl border border-charcoal/10 bg-white" data-testid="admin-leads-table">
          <table className="w-full min-w-[760px] text-left text-sm">
            <thead>
              <tr className="border-b border-charcoal/10 text-xs uppercase tracking-wide text-charcoal/45">
                <th className="px-4 py-3 font-medium">Nome</th>
                <th className="px-4 py-3 font-medium">E-mail</th>
                <th className="px-4 py-3 font-medium">WhatsApp</th>
                <th className="px-4 py-3 font-medium">Salão</th>
                <th className="px-4 py-3 font-medium">Tempo</th>
                <th className="px-4 py-3 font-medium">Equipe</th>
                <th className="px-4 py-3 font-medium">Faturamento</th>
                <th className="px-4 py-3 font-medium">Data</th>
              </tr>
            </thead>
            <tbody>
              {(leads || []).map((l, i) => (
                <tr key={i} className="border-b border-charcoal/[0.05] last:border-0">
                  <td className="px-4 py-3 font-medium text-charcoal">{l.nome}</td>
                  <td className="px-4 py-3 text-charcoal/70">{l.email}</td>
                  <td className="px-4 py-3 text-charcoal/70">{l.whatsapp}</td>
                  <td className="px-4 py-3 text-charcoal/70">{l.trabalha_salao}</td>
                  <td className="px-4 py-3 text-charcoal/70">{l.tempo_beleza}</td>
                  <td className="px-4 py-3 text-charcoal/70">{l.equipe}</td>
                  <td className="px-4 py-3 text-charcoal/70">{l.faturamento}</td>
                  <td className="px-4 py-3 text-charcoal/70">{formatDate(l.created_at)}</td>
                </tr>
              ))}
              {leads && leads.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-charcoal/45">Nenhum cadastro ainda.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
